import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Calendar, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/Navbar';
import axios from 'axios';
import { API } from '@/App';

// Helper to format date for calendar
const formatCalendarDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toISOString().replace(/-|:|\.\d\d\d/g, '').slice(0, -1);
};

// Generate Google Calendar URL
const generateGoogleCalendarUrl = (booking, listing) => {
  const startDate = formatCalendarDate(booking.start_date);
  const endDate = formatCalendarDate(booking.end_date);
  const title = encodeURIComponent(`Parking: ${listing?.title || 'SpotFinder Booking'}`);
  const location = encodeURIComponent(listing?.address || '');
  const details = encodeURIComponent(
    `Booking ID: ${booking.booking_id}\n` +
    `Address: ${listing?.address || 'N/A'}\n` +
    `Access Instructions: ${listing?.access_instructions || 'Check listing for details'}\n\n` +
    `Booked via SpotFinder`
  );
  
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&location=${location}&details=${details}`;
};

// Generate ICS file content for Apple Calendar / Outlook
const generateICSContent = (booking, listing) => {
  const startDate = formatCalendarDate(booking.start_date);
  const endDate = formatCalendarDate(booking.end_date);
  const now = formatCalendarDate(new Date().toISOString());
  
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//SpotFinder//Booking//EN
BEGIN:VEVENT
UID:${booking.booking_id}@spotfinder.co
DTSTAMP:${now}Z
DTSTART:${startDate}Z
DTEND:${endDate}Z
SUMMARY:Parking: ${listing?.title || 'SpotFinder Booking'}
LOCATION:${listing?.address || ''}
DESCRIPTION:Booking ID: ${booking.booking_id}\\nAddress: ${listing?.address || 'N/A'}\\nAccess: ${listing?.access_instructions || 'Check listing'}\\n\\nBooked via SpotFinder
END:VEVENT
END:VCALENDAR`;
};

// Download ICS file
const downloadICS = (booking, listing) => {
  const icsContent = generateICSContent(booking, listing);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `spotfinder-booking-${booking.booking_id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [listingDetails, setListingDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [user, setUser] = useState(null);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const headers = getAuthHeaders();
    axios.get(`${API}/auth/me`, { withCredentials: true, headers })
      .then(res => setUser(res.data))
      .catch(() => {});
  }, []);

  // Fetch booking details when we have a booking ID
  useEffect(() => {
    if (bookingId && status === 'success') {
      const headers = getAuthHeaders();
      axios.get(`${API}/bookings`, { withCredentials: true, headers })
        .then(res => {
          const booking = res.data.find(b => b.booking_id === bookingId);
          if (booking) {
            setBookingDetails(booking);
            // Fetch listing details
            axios.get(`${API}/listings/${booking.listing_id}`)
              .then(listingRes => setListingDetails(listingRes.data))
              .catch(() => {});
          }
        })
        .catch(() => {});
    }
  }, [bookingId, status]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await axios.get(`${API}/payments/stripe/status/${sessionId}`, { 
          withCredentials: true,
          headers 
        });
        
        if (res.data.status === 'paid') {
          setStatus('success');
          setPaymentInfo(res.data);
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <div className="pt-32 pb-12">
        <div className="container-main max-w-lg">
          <Card>
            <CardContent className="pt-12 pb-8 text-center">
              {status === 'checking' && (
                <>
                  <Loader2 className="w-16 h-16 text-[#5ebc67] mx-auto mb-6 animate-spin" />
                  <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    Processing Payment
                  </h1>
                  <p className="text-slate-600">Please wait while we confirm your payment...</p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-[#5ebc67]" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    Booking Confirmed!
                  </h1>
                  <p className="text-slate-600 mb-6">
                    Your parking spot has been booked successfully. You'll receive an SMS confirmation shortly.
                  </p>
                  {paymentInfo && (
                    <p className="text-lg font-semibold text-[#5ebc67] mb-6">
                      Amount Paid: ${paymentInfo.amount?.toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </p>
                  )}
                  
                  {/* Add to Calendar Section */}
                  {bookingDetails && (
                    <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm font-medium text-slate-700 mb-3 flex items-center justify-center gap-2">
                        <CalendarPlus className="w-4 h-4" />
                        Add to Calendar
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(generateGoogleCalendarUrl(bookingDetails, listingDetails), '_blank')}
                          className="rounded-full"
                          data-testid="add-google-calendar"
                        >
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Google
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadICS(bookingDetails, listingDetails)}
                          className="rounded-full"
                          data-testid="add-apple-calendar"
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Apple / Outlook
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => navigate('/driver-bookings')}
                      className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
                      data-testid="view-bookings-btn"
                    >
                      View My Bookings
                    </Button>
                    <Button
                      onClick={() => navigate('/search')}
                      variant="outline"
                      className="rounded-full"
                    >
                      Find More Parking
                    </Button>
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-12 h-12 text-amber-500" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    Confirming Your Payment
                  </h1>
                  <p className="text-slate-600 mb-4">
                    Your payment may have been processed, but we're having trouble confirming it. This can happen due to network issues.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Don't worry!</strong> If you were charged, your booking is likely confirmed. Check your bookings or contact support if you have concerns.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => navigate('/driver-bookings')}
                      className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
                    >
                      Check My Bookings
                    </Button>
                    <Button
                      onClick={() => {
                        setStatus('checking');
                        setAttempts(0);
                      }}
                      variant="outline"
                      className="rounded-full"
                    >
                      Retry Confirmation
                    </Button>
                    <Button
                      onClick={() => navigate('/search')}
                      variant="ghost"
                      className="rounded-full text-slate-500"
                    >
                      Return to Search
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
